// Breeding math. Two modes:
//   - Blending (default): per gene, child allele = round(avg(A, B)). Makes
//     trait mixing intuitive: strong × strong → strong; fast × slow → medium.
//   - Mendelian (blending: false): per gene, child inherits one parent's
//     allele uniformly at random.
//   - In both modes, each allele then has a small chance of mutating by +/-1,
//     clamped to [0, ALLELE_MAX].
//
// Emergent traits are evaluated separately (./traits/emergent.ts) and cached
// per genome; they aren't generated here.

import { clampAllele, type Genome } from './genome';
import { chance, randInt, type Rng } from './rng';

/** Default per-allele mutation probability. ~5% gives ~1 mutation per ~20
 *  alleles, i.e. ~1 mutation per offspring at v1 genome length (24). */
export const DEFAULT_MUTATION_RATE = 0.05;

export interface BreedOptions {
  /** Probability per allele of mutating after inheritance. */
  mutationRate?: number;
  /**
   * true (default): child allele = round(avg(A, B)) — blending inheritance.
   * false: child allele randomly copied from A or B — Mendelian inheritance.
   */
  blending?: boolean;
}

/** Cross two parent genomes into a child. Deterministic given `rng`. */
export function breed(parentA: Genome, parentB: Genome, rng: Rng, opts: BreedOptions = {}): Genome {
  if (parentA.length !== parentB.length) {
    throw new Error(
      `breed: parent genome length mismatch (${parentA.length} vs ${parentB.length})`,
    );
  }
  const mutationRate = opts.mutationRate ?? DEFAULT_MUTATION_RATE;
  const blending = opts.blending ?? true;

  const child = new Array<number>(parentA.length);
  for (let i = 0; i < parentA.length; i++) {
    let allele: number;
    if (blending) {
      allele = Math.round((parentA[i]! + parentB[i]!) / 2);
    } else {
      allele = chance(rng, 0.5) ? parentA[i]! : parentB[i]!;
    }

    // Mutation: chance to drift +/- 1.
    if (chance(rng, mutationRate)) {
      const delta = chance(rng, 0.5) ? 1 : -1;
      allele = clampAllele(allele + delta);
    }

    child[i] = allele;
  }
  return child;
}

/** Random initial population — used at run start. */
export function randomPopulation(rng: Rng, size: number, genomeLength: number): Genome[] {
  const pop: Genome[] = [];
  for (let i = 0; i < size; i++) {
    const g: number[] = new Array(genomeLength);
    for (let j = 0; j < genomeLength; j++) {
      g[j] = randInt(rng, 0, 15);
    }
    pop.push(g);
  }
  return pop;
}
